/*
  The Broadcast — homepage behavior. Post-launch UX rebuild.

  Component lifecycle, not a page-load singleton: every [data-broadcast]
  root is initialized independently and can be destroyed cleanly, so the
  Shopify theme editor's shopify:section:load / shopify:section:unload
  events never leave a duplicated timer, listener, or animation frame
  running behind a section that's been removed or reloaded.

  What changed from the previous version: the hover/focus halo+label
  system tied to the old invisible building navigation is gone — the
  frequency cards are now self-labeled in markup, so there is nothing
  left for JavaScript to reveal on hover. What's left is purely
  atmosphere (skyline silhouette, tower beacon/flicker, drifting dust,
  shooting star) plus one small functional bit: writing the destination
  room to sessionStorage on click, so the destination room's own
  entrance transition (assets/broadcast-rooms.js) can skip a redundant
  fade — unchanged in behavior/format from before, still a same-tab
  hint only, never something navigation depends on.
*/
(function () {
  'use strict';

  var instances = new WeakMap();

  // shorter, fewer buildings than the old full-height skyline — this
  // one only has to read as a skyline within the hero's own height, not
  // the whole viewport. Sixth field ('lit') adds cheap repeating-gradient
  // "windows" via CSS — no extra elements or images.
  var SKYLINE_BUILDINGS = [
    [1, 3.2, 9, .5], [4.5, 2.2, 6, .38, 'mast'], [7.5, 3.6, 12, .55, null, 'lit'],
    [12, 2.4, 7, .4], [15, 2.8, 10, .46, 'tank'],
    [19, 4, 15, .58, null, 'lit'], [24, 2.4, 6.5, .4],
    [27.5, 3.2, 11, .48, null, 'lit'], [32, 2.2, 7.5, .38],
    [36, 2.6, 8, .42],
    [56, 2.8, 8.5, .42], [59.5, 3.8, 13, .55, null, 'lit'],
    [64, 2.4, 6.5, .36, 'mast'], [67.5, 3, 10, .46],
    [71.5, 2.6, 7, .4, null, 'lit'],
    [86, 2.6, 8, .4], [89.5, 4, 14, .55, null, 'lit'], [93.5, 2.2, 6, .34]
  ];

  function initBroadcast(root) {
    if (instances.has(root)) return;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var timers = [];
    var rafId = null;

    function track(id) { timers.push(id); return id; }
    function after(fn, delay) { return track(setTimeout(fn, delay)); }

    var tower = root.querySelector('[data-broadcast-tower]');
    var towerBeacon = root.querySelector('[data-broadcast-beacon]');
    var towerStructure = root.querySelector('[data-broadcast-tower-structure]');
    var maintenanceLight = root.querySelector('[data-broadcast-maintenance-light]');
    var shootingStar = root.querySelector('[data-broadcast-star]');
    var skyline = root.querySelector('[data-broadcast-skyline]');
    var frequencyLinks = Array.prototype.slice.call(root.querySelectorAll('[data-broadcast-frequency]'));

    var shootingStarEnabled = root.dataset.shootingStar !== 'false';

    // ---- background skyline: fixed list, not random, so composition
    // is identical on every load ----
    if (skyline && !skyline.dataset.built) {
      SKYLINE_BUILDINGS.forEach(function (b) {
        var el = document.createElement('div');
        var cls = 'broadcast__skyline-building';
        if (b[4]) cls += ' is-' + b[4];
        if (b[5]) cls += ' is-' + b[5];
        el.className = cls;
        el.style.left = b[0] + '%';
        el.style.width = b[1] + '%';
        el.style.height = b[2] + '%';
        el.style.opacity = b[3];
        skyline.appendChild(el);
      });
      skyline.dataset.built = 'true';
    }

    // ---- camera drift: cancellable rAF loop, not a runaway one ----
    if (!reducedMotion && tower) {
      (function driftTick() {
        var t = Date.now() / 1000;
        var driftX = Math.sin(t / 47) * 2;
        tower.style.transform = 'translateX(calc(-50% + ' + driftX.toFixed(2) + 'px))';
        rafId = window.requestAnimationFrame(driftTick);
      })();
    }

    // ---- beacon: one recursive scheduler, cancelled on destroy ----
    function scheduleBeaconFlash() {
      after(function () {
        if (towerBeacon) {
          towerBeacon.classList.add('is-flashing');
          after(function () { towerBeacon.classList.remove('is-flashing'); }, 1200);
        }
        scheduleBeaconFlash();
      }, 7000 + Math.random() * 10000);
    }
    scheduleBeaconFlash();

    // ---- rare, unforced signs of life ----
    function scheduleRareEvent(fn, minMs, maxMs) {
      after(function () {
        fn();
        scheduleRareEvent(fn, minMs, maxMs);
      }, minMs + Math.random() * (maxMs - minMs));
    }
    if (towerStructure) {
      scheduleRareEvent(function () {
        towerStructure.classList.add('is-flickering');
        after(function () { towerStructure.classList.remove('is-flickering'); }, 550);
      }, 100000, 220000);
    }
    if (maintenanceLight) {
      scheduleRareEvent(function () {
        maintenanceLight.classList.add('is-flickering');
        after(function () { maintenanceLight.classList.remove('is-flickering'); }, 900);
      }, 140000, 280000);
    }

    // ---- shooting star: one scheduler, page-visibility aware ----
    var starCleanupTimer = null;
    function starCleanup() {
      if (shootingStar) shootingStar.classList.remove('is-active');
      clearTimeout(starCleanupTimer);
    }
    function starFire() {
      if (!shootingStar) return;
      var vw = window.innerWidth, vh = window.innerHeight;
      var fromLeft = Math.random() < 0.5;
      var startX = fromLeft ? vw * (0.05 + Math.random() * 0.26) : vw * (0.72 + Math.random() * 0.24);
      var startY = vh * (0.08 + Math.random() * 0.12);
      var dirSign = fromLeft ? 1 : -1;
      var dx = dirSign * (90 + Math.random() * 70);
      var dy = 20 + Math.random() * 28;
      var angleDeg = Math.atan2(dy, dx) * 180 / Math.PI;
      var duration = 700 + Math.random() * 400;
      shootingStar.style.left = startX + 'px';
      shootingStar.style.top = startY + 'px';
      shootingStar.style.setProperty('--star-dx', dx + 'px');
      shootingStar.style.setProperty('--star-dy', dy + 'px');
      shootingStar.style.setProperty('--star-duration', duration + 'ms');
      shootingStar.style.setProperty('--star-angle', angleDeg + 'deg');
      starCleanup();
      void shootingStar.offsetWidth;
      shootingStar.classList.add('is-active');
      starCleanupTimer = after(starCleanup, duration + 150);
    }
    function starSchedule() {
      after(function () {
        if (document.visibilityState === 'visible') starFire();
        starSchedule();
      }, 22000 + Math.random() * 8000);
    }
    if (!reducedMotion && shootingStarEnabled && shootingStar) starSchedule();

    // ---- same-tab hint for the destination room's own entrance settle
    // (assets/broadcast-rooms.js) to skip a redundant fade — written
    // synchronously on click so it lands before the browser follows the
    // link's own real href; never blocks or delays that navigation ----
    var clickHandlers = [];
    frequencyLinks.forEach(function (link) {
      function markIntent() {
        try { window.sessionStorage.setItem('gomez-fm:broadcast-transition-intent', link.dataset.room); } catch (e) { /* ignore */ }
      }
      link.addEventListener('click', markIntent);
      clickHandlers.push({ link: link, markIntent: markIntent });
    });

    instances.set(root, {
      destroy: function () {
        timers.forEach(function (id) { clearTimeout(id); });
        if (rafId) window.cancelAnimationFrame(rafId);
        clickHandlers.forEach(function (h) {
          h.link.removeEventListener('click', h.markIntent);
        });
      }
    });
  }

  function destroyBroadcast(root) {
    var instance = instances.get(root);
    if (instance) {
      instance.destroy();
      instances.delete(root);
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-broadcast]');
    for (var i = 0; i < roots.length; i++) initBroadcast(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var roots = event.target.querySelectorAll('[data-broadcast]');
    for (var i = 0; i < roots.length; i++) destroyBroadcast(roots[i]);
  });
})();
